/**
 * DESIGN.md §8 — the bounded content column ("sticky-bg" width).
 *
 * The marketing layout is a full-bleed canvas: gradients, dark surfaces,
 * and rules span the whole viewport, while the readable content sits in a
 * centered column that caps at the 1440px design frame minus the 64px
 * desktop gutters → a 1312px content box (the same width the nav's
 * floating pill uses). Below the frame, the responsive gutter (24px
 * mobile, 64px desktop) keeps content off the edges.
 *
 * Use `<Bounded>` for any content that should align to this column;
 * keep the full-bleed background element OUTSIDE it (or use the `as`
 * prop to bound an inner row within a full-width parent). Never put a
 * gradient/background on `<Bounded>` itself — backgrounds are full-width.
 */
import type { ElementType, HTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

export interface BoundedProps extends HTMLAttributes<HTMLElement> {
  /** Element to render. Defaults to `div`. */
  as?: ElementType;
  /** Drop the horizontal gutter (e.g. when the parent already pads). */
  flush?: boolean;
}

export function Bounded({ as: As = 'div', flush = false, className, ...props }: BoundedProps) {
  return <As className={cn('mx-auto w-full max-w-[1440px]', flush ? '' : 'px-6 md:px-16', className)} {...props} />;
}
